/**
 * WeaveIn.ai Platform - Security Compliance Dashboard API Service
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ComplianceDashboardMetrics {
  total_break_glass_events: number;
  pending_reviews: number;
  pii_access_count: number;
  active_shields_count: number;
  encryption_status: string;
  violations_count: number;
  external_systems_active: number;
}

export interface BreakGlassTrend {
  month: number;
  year: number;
  count: number;
  pending: number;
  approved: number;
  flagged: number;
  violation: number;
}

export interface AuditLogEntry {
  id: number;
  table_name: string | null;
  record_id: number | null;
  field_accessed: string | null;
  access_type: string | null;
  accessed_by: number | null;
  accessed_by_name: string | null;
  accessed_at: string | null;
  ip_address: string | null;
  sensitivity_tier: string | null;
  data_shield_active: boolean;
}

export interface AuditLogResponse {
  entries: AuditLogEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface AuditLogParams {
  page?: number;
  page_size?: number;
  field?: string;
  user_id?: number;
  date_from?: string;
  date_to?: string;
}

export interface ViolationEntry {
  id: number;
  cbo_id: number;
  initiated_by: number;
  initiator_name: string | null;
  member_id: number | null;
  reason: string;
  justification_category: string | null;
  review_status: string;
  created_at: string | null;
  expires_at: string | null;
  age_hours: number;
}

export interface PolicySummary {
  total_programs: number;
  programs_shield_enabled: number;
  programs_break_glass_enabled: number;
  programs_audit_enabled: number;
  programs_exchange_enabled: number;
  org_shield_enabled: boolean;
  org_encryption_enabled: boolean;
  org_break_glass_enabled: boolean;
}

// ============================================================================
// API CLIENT
// ============================================================================

class ComplianceAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });
  }

  async getDashboardMetrics(cboId: number): Promise<ComplianceDashboardMetrics> {
    const response = await this.client.get<ComplianceDashboardMetrics>(
      `/api/organizations/${cboId}/compliance/dashboard`
    );
    return response.data;
  }

  async getBreakGlassTrends(cboId: number, months?: number): Promise<BreakGlassTrend[]> {
    const response = await this.client.get<BreakGlassTrend[]>(
      `/api/organizations/${cboId}/compliance/break-glass-trends`,
      { params: months ? { months } : undefined }
    );
    return response.data;
  }

  async getAuditLog(cboId: number, params?: AuditLogParams): Promise<AuditLogResponse> {
    const response = await this.client.get<AuditLogResponse>(
      `/api/organizations/${cboId}/compliance/audit-log`,
      { params }
    );
    return response.data;
  }

  async getViolations(cboId: number): Promise<ViolationEntry[]> {
    const response = await this.client.get<ViolationEntry[]>(
      `/api/organizations/${cboId}/compliance/violations`
    );
    return response.data;
  }

  async getPolicySummary(cboId: number): Promise<PolicySummary> {
    const response = await this.client.get<PolicySummary>(
      `/api/organizations/${cboId}/compliance/policy-summary`
    );
    return response.data;
  }
}

export const complianceAPI = new ComplianceAPI();
